type ThemeParams = {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  destructive_text_color?: string;
  accent_text_color?: string;
  section_separator_color?: string;
};

type MainButton = {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  setText(t: string): MainButton;
  show(): MainButton;
  hide(): MainButton;
  enable(): MainButton;
  disable(): MainButton;
  onClick(cb: () => void): MainButton;
  offClick(cb: () => void): MainButton;
  showProgress(leaveActive?: boolean): MainButton;
  hideProgress(): MainButton;
  setParams(p: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }): MainButton;
};

type BackButton = {
  isVisible: boolean;
  show(): BackButton;
  hide(): BackButton;
  onClick(cb: () => void): BackButton;
  offClick(cb: () => void): BackButton;
};

type HapticFeedback = {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
};

export type WebApp = {
  initData: string;
  initDataUnsafe: { user?: { id: number | string; first_name?: string; last_name?: string; username?: string } };
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  MainButton: MainButton;
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(c: string): void;
  setBackgroundColor(c: string): void;
  onEvent(ev: string, cb: () => void): void;
  offEvent(ev: string, cb: () => void): void;
  readTextFromClipboard?(cb: (text: string | null) => void): void;
  // Bot API 6.4+: нативный сканер QR. callback(text) → true закрывает поповер.
  showScanQrPopup?(params: { text?: string }, callback: (text: string) => boolean | void): void;
  closeScanQrPopup?(): void;
  showAlert?(message: string, callback?: () => void): void;
  openLink?(url: string, options?: { try_instant_view?: boolean }): void;
  version?: string;
  isVersionAtLeast?(version: string): boolean;
  // Bot API 6.9+: нативный запрос номера телефона аккаунта. Точная форма ответа зависит
  // от версии клиента — на бэке номер берём из заверенного Contact (webhook), не из клиента.
  requestContact?(callback: (result?: unknown) => void): void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
    WWChat?: { WebApp?: WebApp };
  }
}

export type Platform = "telegram" | "wwchat" | "web";

// Активная платформа: мини-апп открыт в Telegram, в WWChat, или это обычный веб.
// SDK обоих мессенджеров инжектится их клиентом в webview и совместим по форме.
export function platform(): Platform {
  if (typeof window === "undefined") return "web";
  if (window.Telegram?.WebApp?.initData) return "telegram";
  if (window.WWChat?.WebApp?.initData) return "wwchat";
  return "web";
}

// Ждём готовности SDK мессенджера до старта приложения. WWChat/Telegram инжектят SDK
// в webview, а telegram-web-app.js грузится async — поэтому на момент маунта он может
// быть ещё не готов. Ждём появления реального initData (или таймаут → веб-режим).
export function waitForSdk(timeoutMs = 2500): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (platform() !== "web") return Promise.resolve();
  return new Promise((resolve) => {
    const start = Date.now();
    const id = setInterval(() => {
      if (platform() !== "web" || Date.now() - start >= timeoutMs) {
        clearInterval(id);
        resolve();
      }
    }, 50);
  });
}

// Активный WebApp-SDK (Telegram или WWChat). tg() сохраняет имя ради обратной
// совместимости вызовов, но возвращает SDK любого из мессенджеров.
// ВАЖНО: telegram-web-app.js всегда создаёт window.Telegram.WebApp (пустой stub даже
// в WWChat-вебвью), поэтому сначала отдаём платформу с реальным initData.
export function tg(): WebApp | null {
  if (typeof window === "undefined") return null;
  const t = window.Telegram?.WebApp;
  const w = window.WWChat?.WebApp;
  if (t?.initData) return t;
  if (w?.initData) return w;
  return t ?? w ?? null;
}

// Заголовок авторизации для текущей платформы: имя + подписанный initData.
export function initDataHeader(): Record<string, string> {
  const wa = tg();
  const data = wa?.initData ?? "";
  if (!data) return {};
  return platform() === "wwchat"
    ? { "X-WWChat-Init-Data": data }
    : { "X-Telegram-Init-Data": data };
}

// Открывает внешнюю ссылку: в Telegram/WWChat — через openLink (системный браузер,
// где надёжно работает камера/liveness), в вебе — новой вкладкой.
export function openExternal(url: string) {
  const wa = tg();
  if (wa?.openLink) wa.openLink(url);
  else window.open(url, "_blank", "noopener");
}

// Закрывает мини-апп: в Telegram/WWChat — нативный close(); в вебе close() недоступен
// (вкладку открыл пользователь), поэтому пробуем window.close() как best-effort.
export function closeApp() {
  const wa = tg();
  if (platform() !== "web" && wa?.close) wa.close();
  else window.close();
}

// Доступен ли нативный запрос номера (кнопка Telegram). Только в Telegram, метод есть,
// и версия ≥ 6.9 (если клиент её сообщает). Иначе — прячем кнопку, предлагаем звонок/обновление.
export function canRequestContact(): boolean {
  const wa = tg();
  if (!wa || platform() !== "telegram") return false;
  if (typeof wa.requestContact !== "function") return false;
  if (wa.isVersionAtLeast) return wa.isVersionAtLeast("6.9");
  return true;
}

export function tgUserName(): string | null {
  const u = tg()?.initDataUnsafe?.user;
  if (!u) return null;
  const parts = [u.first_name, u.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : u.username ?? null;
}

function setVar(name: string, value?: string) {
  if (value) document.documentElement.style.setProperty(name, value);
}

export function applyTelegramTheme() {
  const wa = tg();
  if (!wa) return;
  const t = wa.themeParams;
  setVar("--tg-bg", t.bg_color);
  setVar("--tg-bg-sec", t.secondary_bg_color);
  setVar("--tg-text", t.text_color);
  setVar("--tg-hint", t.hint_color);
  setVar("--tg-link", t.link_color);
  setVar("--tg-button", t.button_color);
  setVar("--tg-button-text", t.button_text_color);
  setVar("--tg-destructive", t.destructive_text_color);
  setVar("--tg-accent", t.accent_text_color);
  setVar("--tg-separator", t.section_separator_color);
}

export function initTelegram() {
  const wa = tg();
  if (!wa) return;
  wa.ready();
  wa.expand();
  applyTelegramTheme();
  wa.onEvent("themeChanged", applyTelegramTheme);
}
